import { useList } from '@refinedev/core'
import { Button, Card, Input, Modal, Space, Spin, Table, Typography } from 'antd'
import { EyeOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons'
import { useState } from 'react'

import { useMessage } from '@/providers/message-provider'

const { Text } = Typography

/**
 * 项目级设备列表（F6）
 *
 * 通过 useList 调用 GET /api/admin/{projectCode}/devices
 * 顶部按 machineCode 搜索；表格列展示机器码、machineId 短哈希、
 * 首次注册时间、最后活跃时间；点击"查看详情"弹窗展示 machineMeta JSON。
 */

/** 设备实体 */
interface Device {
  id: string
  machineCode: string
  machineId: string
  machineMeta: Record<string, unknown> | null
  firstBoundAt: string | null
  lastSeenAt: string | null
  projectId: string
}

const monoStyle: React.CSSProperties = {
  fontFamily: 'JetBrains Mono, monospace',
  fontSize: 12
}

/** machineId 短哈希：前 8 + ... + 后 8 */
function shortHash(s: string): string {
  if (!s) return '-'
  if (s.length <= 20) return s
  return `${s.slice(0, 8)}...${s.slice(-8)}`
}

/** 格式化时间 */
function fmt(iso?: string | null): string {
  if (!iso) return '-'
  try {
    return new Date(iso).toLocaleString('zh-CN')
  } catch {
    return iso
  }
}

export function DevicesList({ projectCode }: { projectCode: string }) {
  const message = useMessage()
  const [searchCode, setSearchCode] = useState('')
  const [appliedCode, setAppliedCode] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [detailOpen, setDetailOpen] = useState(false)
  const [detailMeta, setDetailMeta] = useState<Device | null>(null)

  const { data, isLoading, isError, refetch } = useList<Device>({
    resource: 'project-devices',
    meta: { projectCode },
    pagination: { current: page, pageSize },
    filters: appliedCode
      ? [{ field: 'machineCode', operator: 'eq', value: appliedCode }]
      : []
  })

  const devices = data?.data ?? []
  const total = data?.total ?? 0

  const handleSearch = () => {
    setAppliedCode(searchCode.trim())
    setPage(1)
  }

  const handleReset = () => {
    setSearchCode('')
    setAppliedCode('')
    setPage(1)
  }

  const showDetail = (d: Device) => {
    setDetailMeta(d)
    setDetailOpen(true)
  }

  if (isError) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Text type="secondary">加载失败</Text>
          <br />
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} style={{ marginTop: 12 }}>
            重试
          </Button>
        </div>
      </Card>
    )
  }

  return (
    <div>
      {/* 顶部搜索区 */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space>
          <Input
            placeholder="按 machineCode 精确搜索"
            value={searchCode}
            onChange={(e) => setSearchCode(e.target.value)}
            onPressEnter={handleSearch}
            allowClear
            style={{ width: 320, fontFamily: 'JetBrains Mono, monospace' }}
            prefix={<SearchOutlined style={{ color: 'var(--ant-color-text-tertiary, #94A3B8)' }} />}
          />
          <Button type="primary" onClick={handleSearch}>
            搜索
          </Button>
          <Button onClick={handleReset}>重置</Button>
          <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
            刷新
          </Button>
        </Space>
      </Card>

      {/* 设备表格 */}
      <Card size="small">
        <Spin spinning={isLoading}>
          <Table<Device>
            rowKey="id"
            dataSource={devices}
            size="small"
            scroll={{ x: 'max-content' }}
            pagination={{
              current: page,
              pageSize,
              total,
              showSizeChanger: true,
              showTotal: (t) => `共 ${t} 条`,
              onChange: (p, ps) => {
                setPage(p)
                setPageSize(ps)
              }
            }}
            columns={[
              {
                title: 'machineCode',
                dataIndex: 'machineCode',
                width: 320,
                render: (v: string) => (
                  <span style={monoStyle} className="tabular-nums">
                    {v}
                  </span>
                )
              },
              {
                title: 'machineId',
                dataIndex: 'machineId',
                width: 220,
                render: (v: string) => (
                  <span style={monoStyle}>{shortHash(v)}</span>
                )
              },
              {
                title: '首次注册时间',
                dataIndex: 'firstBoundAt',
                width: 180,
                render: (v: string | null) => (
                  <span className="tabular-nums" style={{ fontSize: 12 }}>
                    {fmt(v)}
                  </span>
                )
              },
              {
                title: '最后活跃时间',
                dataIndex: 'lastSeenAt',
                width: 180,
                render: (v: string | null) => (
                  <span className="tabular-nums" style={{ fontSize: 12 }}>
                    {fmt(v)}
                  </span>
                )
              },
              {
                title: '操作',
                key: 'op',
                width: 120,
                fixed: 'right',
                render: (_: unknown, record: Device) => (
                  <Button
                    type="link"
                    size="small"
                    icon={<EyeOutlined />}
                    onClick={() => showDetail(record)}
                    style={{ padding: 0 }}
                  >
                    查看详情
                  </Button>
                )
              }
            ]}
          />
        </Spin>
      </Card>

      {/* machineMeta 详情弹窗 */}
      <Modal
        open={detailOpen}
        title="设备元数据详情"
        footer={[
          <Button key="close" onClick={() => setDetailOpen(false)}>
            关闭
          </Button>
        ]}
        onCancel={() => setDetailOpen(false)}
        width={640}
      >
        {detailMeta && (
          <div>
            <div style={{ marginBottom: 12 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                machineCode
              </Text>
              <div style={monoStyle}>{detailMeta.machineCode}</div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                machineId
              </Text>
              <div style={monoStyle}>{detailMeta.machineId}</div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                machineMeta
              </Text>
              <pre
                style={{
                  fontFamily: 'JetBrains Mono, monospace',
                  fontSize: 12,
                  padding: 12,
                  background: 'var(--ant-color-fill-tertiary, #F1F5F9)',
                  borderRadius: 6,
                  maxHeight: 320,
                  overflow: 'auto',
                  margin: '4px 0 0',
                  wordBreak: 'break-all',
                  whiteSpace: 'pre-wrap'
                }}
              >
                {JSON.stringify(detailMeta.machineMeta ?? {}, null, 2)}
              </pre>
            </div>
            <Space size="large">
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  首次注册
                </Text>
                <div className="tabular-nums" style={{ fontSize: 12 }}>
                  {fmt(detailMeta.firstBoundAt)}
                </div>
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  最后活跃
                </Text>
                <div className="tabular-nums" style={{ fontSize: 12 }}>
                  {fmt(detailMeta.lastSeenAt)}
                </div>
              </div>
            </Space>
            <div style={{ marginTop: 12, textAlign: 'right' }}>
              <Button
                size="small"
                onClick={() => {
                  if (!detailMeta?.machineCode) return
                  navigator.clipboard.writeText(detailMeta.machineCode)
                  message.success('已复制 machineCode')
                }}
              >
                复制 machineCode
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}
